import React, { useEffect, useRef } from "react";
import "./InventoryEntry.css";
import { IReducer } from "../../state/reducer";
import { useCanvas } from "../GlobalCanvasManager/CanvasContext";
import { getModelPath } from "../../utils/brickModelPaths";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { calculateBrickPackAutoCollect } from "../../utils/calculateBrickPackAutoCollect";

interface InventoryEntryProps extends IReducer {
  brickId: string;
}

const InventoryEntry: React.FC<InventoryEntryProps> = ({ state, brickId }) => {
  const currBrick = state.bricks.get(brickId)!;
  const currAmount = currBrick.amount;
  const currCapacity = currBrick.capacity;
  const currAuto = currBrick.autoCollect;

  // Calculate brick pack auto-collect contribution
  const packAutoCollect = calculateBrickPackAutoCollect(brickId, state);

  const containerRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLSpanElement>(null);
  const { registerCard, unregisterCard } = useCanvas();

  // Drag and drop functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: brickId, disabled: !state.editModeInventory });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: state.editModeInventory ? "grab" : "default",
  };

  // Register this card with the global canvas manager (only once on mount)
  useEffect(() => {
    if (!visualRef.current) return;

    const cardId = `inventory-${brickId}`;
    const modelPath = getModelPath(brickId);

    // Get actual dimensions from the DOM element
    const rect = visualRef.current.getBoundingClientRect();

    registerCard({
      id: cardId,
      modelPath,
      element: visualRef.current,
      width: rect.width,
      height: rect.height,
    });

    return () => {
      unregisterCard(cardId);
    };
    // Only run once on mount - don't re-register when amounts change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brickId]);

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        setNodeRef(node);
      }}
      style={style}
      {...attributes}
      {...listeners}
      className={`${Number.isNaN(currAmount) ? "inventoryEntry hideEntry" : "inventoryEntry"} ${state.editModeInventory ? "editMode" : ""} ${isDragging ? "dragging" : ""}`}
    >
      <span className="itemVisual" ref={visualRef}>
        {/* 3D model rendered by GlobalCanvasManager - visualRef stays stable */}
      </span>
      <div className="entryDetails">
        <b
          className={`itemTitle ${
            currBrick?.name?.length > 15 ? "smallText" : ""
          }`}
        >
          {currBrick?.name ? currBrick.name : "Default"}
        </b>
        <b className="itemAmount">
          {currAmount} / {currCapacity}
        </b>
        <b className="itemAuto">
          {/* Show individual auto-collect if exists */}
          {currAuto > 0 && `${currAuto} /s`}
          {/* Show separator if both exist */}
          {currAuto > 0 && packAutoCollect.totalAutoPurchases > 0 && " + "}
          {/* Show pack auto-collect with percentage if exists */}
          {packAutoCollect.totalAutoPurchases > 0 &&
            `${packAutoCollect.totalAutoPurchases} /s (${packAutoCollect.averageFindPercentage.toFixed(0)}%)`}
          {/* Show 0 /s with percentage if brick is part of pack but no auto-collect */}
          {currAuto === 0 &&
            packAutoCollect.totalAutoPurchases === 0 &&
            packAutoCollect.isPartOfPack &&
            `0 /s (${packAutoCollect.overallFindPercentage.toFixed(0)}%)`}
          {/* Show 0 /s if not part of any pack */}
          {currAuto === 0 &&
            packAutoCollect.totalAutoPurchases === 0 &&
            !packAutoCollect.isPartOfPack &&
            "0 /s"}
        </b>
      </div>
    </div>
  );
};

export default InventoryEntry;
